<script lang="ts">
    import cn from 'classnames';
    import { tick } from 'svelte';
    import type { Action } from 'svelte/action';
    import { throttle } from 'throttle-debounce';

    import { HALF_MINUTE_IN_MS } from '@shared/constants';

    import s from './video.module.css';

    type ActionParams = $$Generic<Record<string, unknown>>;

    export let element: HTMLVideoElement | undefined = undefined;
    export let src: string | null | undefined = undefined;
    export let poster: string | undefined = undefined;
    export let muted = false;
    export let playsinline = false;
    export let autoplay = false;
    export let loop = false;
    export let preload: 'none' | 'metadata' | 'auto' = 'auto';
    export let controls = false;
    export let covered = false;
    export let attrs: Record<string, string | number | boolean> = {};
    export let action: Action<HTMLVideoElement, ActionParams> = () => undefined;
    export let actionParams: ActionParams = {} as ActionParams;
    export let wrapperClasses = '';
    export let classes = '';
    export let onCanplay: ((event: Event) => void) | undefined = undefined;
    export let mirrored = false;
    export let isHidden = false;

    let wrapper: HTMLElement;
    let coveredPositionClass: string;

    function coveredResize() {
        if (!covered || !wrapper || !element) {
            return;
        }

        const videoParentRatio = wrapper.offsetWidth / wrapper.offsetHeight;
        const videoRatio = element.videoWidth / element.videoHeight;
        coveredPositionClass = videoParentRatio > videoRatio ? s.horizontal : s.vertical;
    }

    const throttleCoveredResize = throttle(HALF_MINUTE_IN_MS, coveredResize);

    $: if (covered) {
        tick().then(() => coveredResize());
    }
</script>

<svelte:window on:resize={throttleCoveredResize} />

<div
    bind:this={wrapper}
    class={cn(s.videoWrapper, wrapperClasses, { [s.videoHidden]: isHidden })}
>
    <video
        class={
            cn(
                s.video,
                { [s.videoCovered]: covered, [coveredPositionClass]: covered, [s.mirrored]: mirrored },
                classes
            )
        }
        bind:this={element}
        bind:muted
        playsinline={playsinline || undefined}
        autoplay={autoplay || undefined}
        loop={loop || undefined}
        controls={controls || undefined}
        {src}
        {poster}
        {preload}
        {...attrs}
        on:canplay={(event) => { if (onCanplay) { onCanplay(event); }; coveredResize(); }}
        on:loadstart
        on:timeupdate
        on:playing
        on:error
        on:click
        on:touchstart
        on:mouseenter
        on:mouseleave
        on:loadedmetadata
        on:waiting
        on:loadeddata
        on:abort
        on:stalled
        on:emptied
        on:suspend
        on:durationchange
        on:progress
        use:action={actionParams}
    >
        <slot />
    </video>
</div>
